import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  faArrowUpRightFromSquare,
  faBolt,
  faCheck,
  faInbox,
  faTriangleExclamation,
  faUserGroup,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import api from "../api/API";
import { useAuth } from "../hooks/useAuth";
import { useWorkspace } from "../context/WorkspaceContext";
import { getTaskPriorityLabel, TASK_PRIORITY, TASK_PRIORITY_OPTIONS } from "../utils/taskPriority";
import "./DashboardGestorPage.css";

const parseCollection = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data?.status === false) {
    throw new Error(response?.data?.message || "Falha ao carregar dados.");
  }
  return response?.data?.records || response?.data?.data || [];
};

const formatDate = (value) => {
  if (!value) return "Sem prazo";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sem prazo";
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
  });
};

const getDueBucket = (value) => {
  if (!value) return "sem-prazo";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);

  if (date < today) return "atrasada";
  if (date.getTime() === today.getTime()) return "hoje";

  const weekLimit = new Date(today);
  weekLimit.setDate(weekLimit.getDate() + 7);
  if (date <= weekLimit) return "semana";
  return "futuro";
};

const priorityToLabel = (priority) => getTaskPriorityLabel(priority);

const DashboardGestorPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, loading: loadingWorkspace, error: workspaceError } = useWorkspace();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingInboxId, setSavingInboxId] = useState(null);
  const [creatingInboxTask, setCreatingInboxTask] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [quickTask, setQuickTask] = useState({
    descricao: "",
    detalhe: "",
    prioridade: String(TASK_PRIORITY.MEDIUM),
  });
  const [inboxDrafts, setInboxDrafts] = useState({});

  const loadData = async () => {
    if (!selectedWorkspaceId) {
      setTasks([]);
      setProjects([]);
      setMembers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const [tasksRes, projectsRes, membersRes] = await Promise.all([
        api.get(`/tasks?workspaceId=${selectedWorkspaceId}`),
        api.get(`/projects?workspaceId=${selectedWorkspaceId}`),
        api.get(`/workspaces/${selectedWorkspaceId}/members`),
      ]);

      const nextTasks = parseCollection(tasksRes);
      const nextProjects = parseCollection(projectsRes);
      const nextMembers = parseCollection(membersRes);

      setTasks(nextTasks);
      setProjects(nextProjects);
      setMembers(nextMembers);
    } catch (error) {
      console.error("DashboardGestorPage: erro ao carregar dados", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel carregar o dashboard gestor."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedWorkspaceId]);

  useEffect(() => {
    const initialDrafts = {};
    tasks.forEach((task) => {
      if (!task.id_projeto) {
        initialDrafts[task.id] = {
          id_projeto: task.id_projeto ? String(task.id_projeto) : "",
          id_usuario_responsavel: task.id_usuario_responsavel ? String(task.id_usuario_responsavel) : "",
          prioridade: task.prioridade ? String(task.prioridade) : String(TASK_PRIORITY.MEDIUM),
          vencimento: task.vencimento ? String(task.vencimento).slice(0, 10) : "",
        };
      }
    });
    setInboxDrafts(initialDrafts);
  }, [tasks]);

  const inboxTasks = useMemo(
    () => tasks.filter((task) => !task.id_projeto && !task.id_tarefa),
    [tasks]
  );

  const activeTasks = useMemo(
    () => tasks.filter((task) => !task.concluido && !task.id_tarefa),
    [tasks]
  );

  const metrics = useMemo(() => {
    const overdue = activeTasks.filter((task) => getDueBucket(task.vencimento) === "atrasada").length;
    const today = activeTasks.filter((task) => getDueBucket(task.vencimento) === "hoje").length;
    const withoutOwner = activeTasks.filter((task) => !task.id_usuario_responsavel).length;
    return {
      total: activeTasks.length,
      overdue,
      today,
      withoutOwner,
      inbox: inboxTasks.length,
    };
  }, [activeTasks, inboxTasks]);

  const peopleStats = useMemo(() => {
    const grouped = new Map();

    activeTasks.forEach((task) => {
      const memberId = String(task.id_usuario_responsavel || "sem-dono");
      const memberName =
        task.responsavel ||
        members.find((member) => String(member.id_usuario ?? member.id) === memberId)?.descricao ||
        members.find((member) => String(member.id_usuario ?? member.id) === memberId)?.nome ||
        "Sem dono";

      if (!grouped.has(memberId)) {
        grouped.set(memberId, {
          id: memberId,
          nome: memberName,
          total: 0,
          overdue: 0,
          today: 0,
          projects: new Set(),
        });
      }

      const current = grouped.get(memberId);
      current.total += 1;
      current.projects.add(task.projeto || "Sem projeto");

      const bucket = getDueBucket(task.vencimento);
      if (bucket === "atrasada") current.overdue += 1;
      if (bucket === "hoje") current.today += 1;
    });

    return Array.from(grouped.values())
      .map((entry) => ({
        ...entry,
        projects: entry.projects.size,
      }))
      .sort((left, right) => {
        if (right.overdue !== left.overdue) return right.overdue - left.overdue;
        return right.total - left.total;
      });
  }, [activeTasks, members]);

  const urgentTasks = useMemo(() => {
    return [...activeTasks]
      .sort((left, right) => {
        const weight = { atrasada: 3, hoje: 2, semana: 1, futuro: 0, "sem-prazo": -1 };
        const dueDiff = (weight[getDueBucket(right.vencimento)] || 0) - (weight[getDueBucket(left.vencimento)] || 0);
        if (dueDiff !== 0) return dueDiff;
        return Number(left.prioridade || 99) - Number(right.prioridade || 99);
      })
      .slice(0, 8);
  }, [activeTasks]);

  const handleCreateInboxTask = async () => {
    if (!selectedWorkspaceId || !quickTask.descricao.trim()) return;

    setCreatingInboxTask(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await api.post("/tasks", {
        fields: {
          descricao: quickTask.descricao.trim(),
          detalhe: quickTask.detalhe.trim() || null,
          id_workspace: selectedWorkspaceId,
          prioridade: Number(quickTask.prioridade || TASK_PRIORITY.MEDIUM),
          id_usuario_responsavel: user?.id || null,
          concluido: false,
        },
      });

      setQuickTask({ descricao: "", detalhe: "", prioridade: String(TASK_PRIORITY.MEDIUM) });
      setSuccessMessage("Demanda capturada no inbox do workspace.");
      loadData();
    } catch (error) {
      console.error("DashboardGestorPage: erro ao criar tarefa de inbox", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel capturar a demanda no inbox."
      );
    } finally {
      setCreatingInboxTask(false);
    }
  };

  const handleInboxDraftChange = (taskId, field, value) => {
    setInboxDrafts((current) => ({
      ...current,
      [taskId]: {
        ...current[taskId],
        [field]: value,
      },
    }));
  };

  const handleClassifyInboxTask = async (task) => {
    const draft = inboxDrafts[task.id];
    if (!draft?.id_projeto) {
      setErrorMessage("Selecione um projeto antes de classificar a demanda.");
      return;
    }

    setSavingInboxId(task.id);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await api.put(`/tasks/${task.id}`, {
        fields: {
          id_workspace: selectedWorkspaceId,
          id_projeto: Number(draft.id_projeto),
          id_usuario_responsavel: draft.id_usuario_responsavel ? Number(draft.id_usuario_responsavel) : null,
          prioridade: draft.prioridade ? Number(draft.prioridade) : null,
          vencimento: draft.vencimento || null,
        },
      });

      setSuccessMessage("Demanda classificada com sucesso.");
      loadData();
    } catch (error) {
      console.error("DashboardGestorPage: erro ao classificar inbox", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel classificar a demanda."
      );
    } finally {
      setSavingInboxId(null);
    }
  };

  const concluirTask = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, { fields: { concluido: true } });
      loadData();
    } catch (error) {
      console.error("DashboardGestorPage: erro ao concluir tarefa", error);
      setErrorMessage("Nao foi possivel concluir a tarefa.");
    }
  };

  return (
    <Container fluid className="gestor-page">
      <section className="gestor-hero">
        <div>
          <p className="gestor-kicker">Manager cockpit</p>
          <h1>Dashboard do Gestor</h1>
          <p className="gestor-subtitle">
            Uma central para ver gargalos, acompanhar a carga do time e capturar demanda solta antes que ela se perca.
          </p>
        </div>

        <div className="gestor-hero-side">
          <div className="gestor-pill">
            <span>Workspace ativo</span>
            <strong>{selectedWorkspace?.nome || "Nenhum selecionado"}</strong>
          </div>
          <div className="gestor-note">
            Toda tarefa criada sem etapa deve cair no <strong>Backlog</strong> automaticamente para triagem.
          </div>
        </div>
      </section>

      {workspaceError && <Alert variant="warning">{workspaceError}</Alert>}
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      {!selectedWorkspaceId && !loadingWorkspace ? (
        <Alert variant="info">Selecione um workspace para abrir o dashboard gestor.</Alert>
      ) : loading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" variant="dark" />
        </div>
      ) : (
        <>
          <Row className="g-3 gestor-metrics">
            <Col md={6} xl={2}>
              <Card className="gestor-metric-card">
                <Card.Body>
                  <span>Ativas</span>
                  <strong>{metrics.total}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="gestor-metric-card warning">
                <Card.Body>
                  <span>Inbox</span>
                  <strong>{metrics.inbox}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="gestor-metric-card danger">
                <Card.Body>
                  <span>Atrasadas</span>
                  <strong>{metrics.overdue}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="gestor-metric-card">
                <Card.Body>
                  <span>Hoje</span>
                  <strong>{metrics.today}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="gestor-metric-card neutral">
                <Card.Body>
                  <span>Sem dono</span>
                  <strong>{metrics.withoutOwner}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="gestor-metric-card insight">
                <Card.Body>
                  <span>Time ativo</span>
                  <strong>{peopleStats.length}</strong>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-4">
            <Col xl={8}>
              <Card className="gestor-panel gestor-inbox-panel">
                <Card.Body>
                  <div className="gestor-panel-header">
                    <div>
                      <h4>
                        <FontAwesomeIcon icon={faInbox} /> Inbox de Captura
                      </h4>
                      <p>Centralize demandas que chegam por WhatsApp, reunião, ligação ou áudio antes de classificar.</p>
                    </div>
                    <Badge bg="warning" text="dark">{inboxTasks.length} pendente(s)</Badge>
                  </div>

                  <div className="gestor-capture-box">
                    <Form.Control
                      value={quickTask.descricao}
                      onChange={(event) => setQuickTask((current) => ({ ...current, descricao: event.target.value }))}
                      placeholder="Ex.: Cliente pediu ajuste no fluxo de cobrança"
                    />
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={quickTask.detalhe}
                      onChange={(event) => setQuickTask((current) => ({ ...current, detalhe: event.target.value }))}
                      placeholder="Contexto rápido, link, canal de origem ou detalhes úteis para depois classificar"
                    />
                    <div className="gestor-capture-actions">
                      <Form.Select
                        value={quickTask.prioridade}
                        onChange={(event) => setQuickTask((current) => ({ ...current, prioridade: event.target.value }))}
                      >
                        {TASK_PRIORITY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Select>
                      <Button onClick={handleCreateInboxTask} disabled={creatingInboxTask}>
                        <FontAwesomeIcon icon={faBolt} /> {creatingInboxTask ? "Capturando..." : "Capturar no inbox"}
                      </Button>
                    </div>
                  </div>

                  <div className="gestor-inbox-list">
                    {inboxTasks.length === 0 ? (
                      <div className="gestor-empty-state">
                        Seu inbox esta limpo. Bom sinal de fluxo organizado.
                      </div>
                    ) : (
                      inboxTasks.map((task) => {
                        const draft = inboxDrafts[task.id] || {};

                        return (
                          <article key={task.id} className="gestor-inbox-card">
                            <div className="gestor-inbox-copy">
                              <h5>{task.descricao}</h5>
                              <p>{task.detalhe || "Sem detalhe adicional."}</p>
                              <div className="gestor-inbox-meta">
                                <Badge bg="light" text="dark">{priorityToLabel(task.prioridade)}</Badge>
                                <span>{formatDate(task.criado_em || task.created_at)}</span>
                              </div>
                            </div>

                            <div className="gestor-inbox-form">
                              <Form.Select
                                value={draft.id_projeto || ""}
                                onChange={(event) => handleInboxDraftChange(task.id, "id_projeto", event.target.value)}
                              >
                                <option value="">Escolha o projeto</option>
                                {projects.map((project) => (
                                  <option key={project.id} value={project.id}>
                                    {project.descricao}
                                  </option>
                                ))}
                              </Form.Select>

                              <Form.Select
                                value={draft.id_usuario_responsavel || ""}
                                onChange={(event) => handleInboxDraftChange(task.id, "id_usuario_responsavel", event.target.value)}
                              >
                                <option value="">Responsavel</option>
                                {members.map((member) => (
                                  <option key={member.id_usuario ?? member.id} value={member.id_usuario ?? member.id}>
                                    {member.descricao || member.nome || member.email}
                                  </option>
                                ))}
                              </Form.Select>

                              <Form.Select
                                value={draft.prioridade || String(TASK_PRIORITY.MEDIUM)}
                                onChange={(event) => handleInboxDraftChange(task.id, "prioridade", event.target.value)}
                              >
                                {TASK_PRIORITY_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </Form.Select>

                              <Form.Control
                                type="date"
                                value={draft.vencimento || ""}
                                onChange={(event) => handleInboxDraftChange(task.id, "vencimento", event.target.value)}
                              />

                              <Button
                                variant="dark"
                                onClick={() => handleClassifyInboxTask(task)}
                                disabled={savingInboxId === task.id}
                              >
                                <FontAwesomeIcon icon={faWandMagicSparkles} />{" "}
                                {savingInboxId === task.id ? "Classificando..." : "Classificar"}
                              </Button>
                            </div>
                          </article>
                        );
                      })
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={4}>
              <Card className="gestor-panel">
                <Card.Body>
                  <div className="gestor-panel-header">
                    <div>
                      <h4>
                        <FontAwesomeIcon icon={faUserGroup} /> Saude do time
                      </h4>
                      <p>Veja rapidamente quem esta puxando mais carga e onde ha risco de gargalo.</p>
                    </div>
                  </div>

                  <div className="gestor-people-list">
                    {peopleStats.length === 0 ? (
                      <div className="gestor-empty-state">Sem tarefas distribuidas no momento.</div>
                    ) : (
                      peopleStats.map((person) => (
                        <div key={person.id} className="gestor-person-card">
                          <div className="gestor-person-header">
                            <strong>{person.nome}</strong>
                            <Badge bg={person.overdue > 0 ? "danger" : "dark"}>
                              {person.total} tarefa(s)
                            </Badge>
                          </div>
                          <div className="gestor-person-stats">
                            <span>{person.overdue} atrasada(s)</span>
                            <span>{person.today} para hoje</span>
                            <span>{person.projects} projeto(s)</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Card.Body>
              </Card>

              <Card className="gestor-panel gestor-urgent-panel mt-4">
                <Card.Body>
                  <div className="gestor-panel-header">
                    <div>
                      <h4>
                        <FontAwesomeIcon icon={faTriangleExclamation} /> Radar de urgencia
                      </h4>
                      <p>As tarefas que mais merecem sua atencao agora.</p>
                    </div>
                  </div>

                  <div className="gestor-urgent-list">
                    {urgentTasks.map((task) => (
                      <div key={task.id} className="gestor-urgent-card">
                        <div>
                          <strong>{task.descricao}</strong>
                          <small>{task.projeto || "Sem projeto"} • {task.responsavel || "Sem dono"}</small>
                        </div>
                        <div className="gestor-urgent-actions">
                          <Badge bg={getDueBucket(task.vencimento) === "atrasada" ? "danger" : "warning"}>
                            {formatDate(task.vencimento)}
                          </Badge>
                          {!task.concluido && (
                            <Button size="sm" variant="success" onClick={() => concluirTask(task.id)}>
                              <FontAwesomeIcon icon={faCheck} />
                            </Button>
                          )}
                          <Button size="sm" variant="outline-dark" onClick={() => navigate(`/projetos/${task.id_projeto}`)}>
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default DashboardGestorPage;
